import { useMemo, type ReactNode } from 'react';
import { getMeta } from '../api/endpoints';
import type { CrmCapabilities } from '../api/types';
import { CapabilitiesContext, type CapabilitiesState } from '../lib/capabilities';
import { TenantDashboardContext } from '../lib/tenantDashboard';
import { useLoad } from '../lib/useLoad';

/**
 * Loads the signed-in agent's grants from `GET /meta` and hands them to the whole
 * shell (see lib/capabilities).
 *
 * A separate fetch from TenantTimeProvider's, deliberately: the timezone is set
 * once in dispatch and never moves, while grants change whenever an admin edits a
 * role or a membership, and the socket's `access.changed` has to be able to pull
 * a fresh copy without disturbing every rendered timestamp.
 *
 * A failed load is not an error state. `me` stays null, every helper in
 * lib/capabilities answers permissively, and the server keeps enforcing — a slow
 * metadata call must not grey out a working console.
 *
 * The same read carries the tenant's dashboard address (lib/tenantDashboard),
 * handed down beside the grants rather than fetched again.
 *
 * `capabilities` short-circuits the fetch for fixtures and previews, with
 * `dashboardUrl` as that fixture's dashboard.
 */
export function CapabilitiesProvider({
  children,
  capabilities,
  dashboardUrl = null,
}: {
  children: ReactNode;
  capabilities?: CrmCapabilities | null;
  dashboardUrl?: string | null;
}) {
  const load = useLoad(
    () =>
      capabilities !== undefined
        ? Promise.resolve({ me: capabilities, dashboardUrl })
        : getMeta()
            .then((meta) => ({ me: meta.me ?? null, dashboardUrl: meta.dashboardUrl ?? null }))
            .catch(() => ({ me: null, dashboardUrl: null })),
    [capabilities, dashboardUrl],
  );

  const value = useMemo<CapabilitiesState>(
    () => ({ me: load.data?.me ?? null, loading: load.loading, reload: load.reload }),
    [load.data, load.loading, load.reload],
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      <TenantDashboardContext.Provider value={load.data?.dashboardUrl ?? null}>
        {children}
      </TenantDashboardContext.Provider>
    </CapabilitiesContext.Provider>
  );
}
